/**
 * Service Media — proof-of-service evidence captured by the mechanic on
 * the field, displayed to the SA who's monitoring the job.
 *
 * Source of truth: lead.proof_checklist[] (computed server-side from the
 * service package's items + their media_requirement, plus any spare
 * parts added on the lead). Renders one row per item in package order,
 * each showing required vs uploaded positions and the media itself.
 *
 *   • Photo positions → square thumbnail tile (click to zoom)
 *   • Video positions → poster frame + play overlay (click to lightbox)
 *   • Missing positions → dashed "Pending" placeholder tile
 *
 * No upload happens here — this surface is read-only verification for
 * the SA. The mechanic uploads from the React Native app.
 */
import React, { useMemo, useState } from 'react';
import { Image, Video, Camera, ChevronRight, CheckCircle2, Clock, X } from 'lucide-react';
import { toFirebaseDownloadUrl } from '../../../utils/firebaseUrl';

const POS_LABEL = { before: 'Before', after: 'After', single: 'Video' };

function MediaTile({ media, position, mediaType, onOpen }) {
  // mediaType is 'photo' or 'video' (from item.media_requirement.type)
  // Fixed tile size — square, 160px each. Tiles flex-wrap inside the
  // item row container so any combo (1 / 2 / 'single') lays out cleanly
  // without exploding to fill the viewport (was the 2026-05-26 bug).
  const TILE_SIZE = "h-40 w-40";
  if (!media) {
    return (
      <div
        className={`${TILE_SIZE} flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-2 py-3 text-center`}
        data-testid={`media-tile-pending-${position}`}
      >
        {mediaType === 'video' ? (
          <Video className="h-6 w-6 text-slate-400" />
        ) : (
          <Camera className="h-6 w-6 text-slate-400" />
        )}
        <span className="text-[10px] font-medium uppercase tracking-wider text-slate-500">
          {POS_LABEL[position] || position}
        </span>
        <span className="text-[10px] text-amber-600">
          <Clock className="inline h-3 w-3 -mt-0.5 mr-0.5" />
          Pending
        </span>
      </div>
    );
  }

  const isVideo =
    media.mediaType === 'video' ||
    media.media_type === 'video' ||
    mediaType === 'video' ||
    (media.mime_type || '').toLowerCase().startsWith('video') ||
    (media.url || '').toLowerCase().match(/\.(mp4|mov|webm|m4v)(\?|$)/);

  // Firebase Storage gs:// URIs aren't directly loadable in img/video tags —
  // rewrite to the download API endpoint.
  const downloadUrl = toFirebaseDownloadUrl(media.url);

  return (
    <button
      type="button"
      onClick={() => onOpen({ ...media, url: downloadUrl }, isVideo ? 'video' : 'image')}
      className={`group relative ${TILE_SIZE} overflow-hidden rounded-lg border border-slate-300 bg-black hover:ring-2 hover:ring-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500`}
      data-testid={`media-tile-${position}`}
    >
      {isVideo ? (
        <>
          <video
            src={downloadUrl}
            className="h-full w-full object-cover"
            preload="metadata"
            muted
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition group-hover:bg-black/20">
            <div className="rounded-full bg-white/90 p-2 shadow-md">
              <Video className="h-4 w-4 text-slate-900" />
            </div>
          </div>
        </>
      ) : (
        <img
          src={downloadUrl}
          alt={POS_LABEL[position] || position}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      )}
      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-white">
          {POS_LABEL[position] || position}
        </span>
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
      </div>
    </button>
  );
}

function Lightbox({ open, mediaUrl, mediaKind, label, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-6"
      onClick={onClose}
      data-testid="media-lightbox"
    >
      <button
        type="button"
        className="absolute right-6 top-6 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
        onClick={onClose}
        data-testid="lightbox-close"
      >
        <X className="h-5 w-5" />
      </button>
      {label && (
        <div className="absolute top-6 left-6 rounded bg-white/10 px-3 py-1.5 text-sm font-medium text-white">
          {label}
        </div>
      )}
      <div className="max-h-[88vh] max-w-[92vw]" onClick={e => e.stopPropagation()}>
        {mediaKind === 'video' ? (
          <video src={mediaUrl} controls autoPlay className="max-h-[88vh] max-w-[92vw] rounded-md" />
        ) : (
          <img src={mediaUrl} alt={label || 'media'} className="max-h-[88vh] max-w-[92vw] rounded-md object-contain" />
        )}
      </div>
    </div>
  );
}

export default function ServiceMediaTab({ lead }) {
  const [lightbox, setLightbox] = useState({ open: false, url: '', kind: 'image', label: '' });

  // ── PRIMARY: package-driven checklist (server-built proof_checklist) ──
  const checklist = useMemo(() => lead?.proof_checklist || [], [lead]);
  const mediaById = useMemo(() => {
    const map = {};
    (lead?.service_media || []).forEach(m => {
      if (m.item_key && m.position) {
        map[m.item_key] = map[m.item_key] || {};
        map[m.item_key][m.position] = m;
      }
    });
    return map;
  }, [lead]);

  // ── FALLBACK: when proof_checklist is empty but the mechanic app has
  //    already uploaded files, synthesise a checklist directly from
  //    service_media[].category so the SA sees the actual evidence.
  //    (The mechanic-app uses a default 4-item template — Old parts,
  //    Engine oil, New parts, Odometer — keyed by `category` rather than
  //    package-item `item_key`. Without this branch those uploads stay
  //    invisible in CRM even though they're in DB.)
  const CATEGORY_META = {
    old_parts:        { label: "Old / used spare parts",   media_type: "photo", desc: "Photo of removed parts as proof of service" },
    new_parts:        { label: "New spare parts installed",media_type: "photo", desc: "Photo of newly fitted parts" },
    oil_change_video: { label: "Engine oil change",        media_type: "video", desc: "Short video of oil being drained / refilled" },
    odometer:         { label: "Odometer reading",         media_type: "photo", desc: "Clear photo of the odometer at service end" },
    obd_scan:         { label: "OBD scan",                 media_type: "photo", desc: "OBD-II scan report" },
  };
  const syntheticChecklist = useMemo(() => {
    const media = lead?.service_media || [];
    if (!media.length) return [];
    // group by category
    const byCat = {};
    media.forEach(m => {
      const cat = m.category || m.item_key || "other";
      (byCat[cat] = byCat[cat] || []).push(m);
    });
    return Object.entries(byCat).map(([cat, items]) => {
      const meta = CATEGORY_META[cat] || {
        label: String(cat).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        media_type: items[0]?.mime_type?.startsWith("video") ? "video" : "photo",
        desc: null,
      };
      return {
        key: `cat-${cat}`,
        label: meta.label,
        description: meta.desc,
        source: "mechanic_upload",
        media_type: meta.media_type,
        required_positions: ["single"],
        uploaded_positions: ["single"],
        _items: items,            // the raw uploads, used by the renderer below
        _category: cat,
      };
    });
  }, [lead]);

  // Resolve which checklist to render and a helper to look up the media
  // for any given checklist row.
  const effectiveChecklist = checklist.length ? checklist : syntheticChecklist;
  const usingSynthetic = !checklist.length && syntheticChecklist.length > 0;

  const findMedia = (item, position) => {
    // Synthetic rows carry their media inline
    if (item._items) {
      return item._items.find(m => (m.position || "single") === position) || item._items[0];
    }
    // Package-driven rows look up via key+position
    return mediaById[item.key]?.[position];
  };

  const totals = useMemo(() => {
    let required = 0, uploaded = 0;
    effectiveChecklist.forEach(c => {
      const reqs = c.required_positions || [];
      required += reqs.length;
      const got = new Set(c.uploaded_positions || []);
      uploaded += reqs.filter(p => got.has(p)).length;
    });
    return { required, uploaded };
  }, [effectiveChecklist]);

  const openMedia = (media, kind) => {
    setLightbox({
      open: true,
      url: media.url,
      kind,
      label: media.label || '',
    });
  };

  if (!effectiveChecklist.length) {
    return (
      <div className="px-6 py-12 text-center text-sm text-slate-500" data-testid="service-media-empty">
        <Camera className="mx-auto mb-3 h-8 w-8 text-slate-300" />
        <p className="font-medium text-slate-600">No media uploaded for this job yet</p>
        <p className="mt-1 max-w-md mx-auto text-xs">
          Either the mechanic hasn't captured proof-of-service yet, OR this
          package's items don't have a photo/video proof requirement
          configured. Edit the package in Service Packages to enable
          per-item media capture going forward.
        </p>
      </div>
    );
  }

  return (
    <div className="px-6 py-5 space-y-4" data-testid="service-media-tab">
      {/* Header / counter */}
      <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Service Media</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Proof-of-service evidence captured by the mechanic, item by item.
          </p>
          {usingSynthetic && (
            <p className="mt-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 inline-block">
              ⚠ Showing mechanic-app default checklist — this package's items
              don't have media requirements configured yet.
            </p>
          )}
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold tabular-nums text-slate-900">
            {totals.uploaded}<span className="text-base font-normal text-slate-400">/{totals.required}</span>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">media captured</div>
        </div>
      </div>

      {/* Item rows in package order */}
      <div className="space-y-3">
        {effectiveChecklist.map((item, idx) => {
          const reqs = item.required_positions || [];
          const got = new Set(item.uploaded_positions || []);
          const complete = reqs.every(p => got.has(p));
          const mediaType = item.media_type || (reqs.includes('single') ? 'video' : 'photo');

          return (
            <div
              key={item.key}
              className="rounded-lg border border-slate-200 bg-white"
              data-testid={`media-item-${item.key}`}
            >
              <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-2.5">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold tabular-nums text-slate-400">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <h4 className="truncate text-sm font-semibold text-slate-800">{item.label}</h4>
                    {item.source === 'spare' && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                        Spare part
                      </span>
                    )}
                    {item.source === 'mechanic_upload' && (
                      <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-medium text-sky-700">
                        Mechanic upload
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <p className="mt-0.5 truncate text-xs text-slate-500">{item.description}</p>
                  )}
                </div>
                <div className="shrink-0">
                  {complete ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700">
                      <CheckCircle2 className="h-3 w-3" /> Complete
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-medium text-amber-700">
                      <Clock className="h-3 w-3" /> {got.size}/{reqs.length}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-3 p-3">
                {reqs.map(pos => (
                  <MediaTile
                    key={pos}
                    media={findMedia(item, pos)}
                    position={pos}
                    mediaType={mediaType}
                    onOpen={openMedia}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Lightbox
        open={lightbox.open}
        mediaUrl={lightbox.url}
        mediaKind={lightbox.kind}
        label={lightbox.label}
        onClose={() => setLightbox({ open: false, url: '', kind: 'image', label: '' })}
      />
    </div>
  );
}
